import FadeUpText from "@/components/ui/FadeUp";
import SleekButton from "@/components/ui/NavButtons";
import * as React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { HeroDashboardMock } from "./HeroDashboardMock";

export const HeroSection: React.FC = () => {
  const [ripple, setRipple] = React.useState<{ x: number; y: number; id: number } | null>(null);

  const handlePreviewClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const next = { x: e.clientX - rect.left, y: e.clientY - rect.top, id: Date.now() };
    setRipple(next);
    window.setTimeout(() => {
      setRipple((prev) => (prev?.id === next.id ? null : prev));
    }, 550);
  };

  return (
    <section className="relative flex w-full min-h-[calc(100dvh-3.5rem-env(safe-area-inset-top,0px))] flex-col items-center justify-center gap-8 overflow-x-hidden bg-vw-bg-light py-8 dark:bg-vw-bg max-sm:justify-start max-sm:gap-6 max-sm:py-6 sm:min-h-[calc(100dvh-4rem-env(safe-area-inset-top,0px))] md:min-h-[calc(100dvh-72px-env(safe-area-inset-top,0px))] md:gap-10 md:py-10 md:max-xl:gap-8 md:max-xl:py-8 lg:min-h-[calc(100dvh-5rem-env(safe-area-inset-top,0px))] lg:flex-row lg:items-center lg:justify-between lg:gap-12 lg:py-12 lg:max-xl:py-10 xl:min-h-[calc(100dvh-84px-env(safe-area-inset-top,0px))] xl:max-[1366px]:py-10 pl-[max(0.5rem,env(safe-area-inset-left,0px))] pr-[max(0.5rem,env(safe-area-inset-right,0px))] sm:pl-[max(1.25rem,env(safe-area-inset-left,0px))] sm:pr-[max(1.25rem,env(safe-area-inset-right,0px))] md:px-10 lg:px-14 xl:px-20">

      {/* Ambient glow orbs */}
      <div className="absolute top-[-5%] left-[-5%] w-[600px] h-[600px] dark:bg-blue-600/20 bg-blue-400/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[10%] right-[10%] w-[400px] h-[400px] dark:bg-indigo-600/15 bg-indigo-400/10 rounded-full blur-[100px] pointer-events-none" />

      {/* Light mode subtle grid */}
      <div className="absolute inset-0 dark:hidden pointer-events-none opacity-20"
        style={{
          backgroundImage: "linear-gradient(rgba(59,130,246,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.1) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* LEFT — copy */}
      <div className="min-w-0 flex-1 max-w-[520px] max-sm:max-w-none relative z-10 max-md:text-center">
        <FadeUpText>
          <div className="inline-flex px-3 py-1.5 text-xs font-bold tracking-widest dark:text-blue-400 text-blue-700 uppercase dark:bg-blue-600/15 bg-blue-100 rounded-full mb-5 border dark:border-blue-500/20 border-blue-200 max-md:flex max-md:w-fit max-md:mx-auto">
            The Platform
          </div>
        </FadeUpText>

        <FadeUpText className="mb-5 text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight max-sm:text-3xl max-sm:text-center">
          <span className="vw-gradient-text">Two modules.</span>
          <br className="hidden md:block" />
          <span className="dark:text-white text-gray-900">Complete financial</span>
          <br className="hidden md:block" />
          <span className="dark:text-white text-gray-900">intelligence.</span>
        </FadeUpText>

        <FadeUpText className="mb-8 text-lg leading-relaxed dark:text-slate-300 text-gray-600 max-sm:text-base max-sm:text-center max-w-[460px]">
          WealthScope tells you when you're ready to invest. PortfolioIQ tells you how your investments are performing. Together, everything a data-driven real estate investor needs.
        </FadeUpText>

        <div className="flex flex-wrap gap-3 max-sm:justify-center">
          <a href="https://app.vawena.ai/signup" target="_self" rel="noopener noreferrer">
            <SleekButton variant="primary">Start Free Trial</SleekButton>
          </a>
          <Link to="/pricing">
            <SleekButton variant="light">View Pricing</SleekButton>
          </Link>
        </div>

        <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs dark:text-slate-400 text-gray-500 max-md:justify-center">
          {["14-day free trial", "Cancel anytime", "17% off annual"].map(t => (
            <span key={t} className="flex items-center gap-1.5">
              <span className="w-1 h-1 bg-blue-500 rounded-full" />
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* RIGHT — real app screenshot */}
      <div className="flex min-h-0 w-full max-sm:max-w-full flex-1 items-center justify-center relative z-10 max-md:mt-2 lg:w-auto">
        {/* Browser chrome frame */}
        <motion.div
          whileHover={{ scale: 1.012 }}
          whileTap={{ scale: 0.995 }}
          onClick={handlePreviewClick}
          className="group relative w-full max-w-[600px] rounded-2xl overflow-hidden border dark:border-white/[0.08] border-gray-200 shadow-2xl dark:shadow-blue-900/30 max-sm:max-w-full cursor-pointer"
        >
          {/* Browser bar */}
          <div className="flex items-center gap-2 px-4 py-2.5 dark:bg-vw-surface bg-gray-100 border-b dark:border-white/[0.06] border-gray-200">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400/80" />
              <div className="w-3 h-3 rounded-full bg-yellow-400/80" />
              <div className="w-3 h-3 rounded-full bg-green-400/80" />
            </div>
            <div className="flex-1 flex justify-center">
              <div className="flex items-center gap-1.5 px-3 py-1 rounded dark:bg-white/[0.05] bg-white border dark:border-white/10 border-gray-200 text-xs dark:text-slate-400 text-gray-500">
                <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 12 12">
                  <path d="M6 0a6 6 0 100 12A6 6 0 006 0zm.75 8.5H5.25V5.25h1.5V8.5zm0-4.25H5.25V2.75h1.5V4.25z"/>
                </svg>
                app.vawena.ai
              </div>
            </div>
          </div>
          {/* Animated dashboard mock */}
          <div className="max-sm:h-[min(32dvh,260px)] h-[320px] sm:h-[360px] lg:h-[380px] w-full transition-transform duration-500 group-hover:scale-[1.01]">
            <HeroDashboardMock />
          </div>
          <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 bg-gradient-to-tr from-blue-500/10 via-transparent to-cyan-400/10" />
          {ripple && (
            <span
              key={ripple.id}
              className="pointer-events-none absolute h-20 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/60 bg-white/20 animate-ping"
              style={{ left: ripple.x, top: ripple.y }}
            />
          )}
        </motion.div>
      </div>
    </section>
  );
};
